import React, { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'

// Full-screen body-photo preview. Rendered into document.body via a
// portal so `position: fixed` is anchored to the viewport — PhotosTab
// lives inside PullToRefresh, which applies a CSS transform to its
// wrapper, and a transformed ancestor reparents fixed descendants to
// itself. Without the portal the dialog stretches to the full page
// height instead of the viewport, leaving huge empty bands above and
// below the centered image.
export function PhotoLightbox({ photo, onClose }) {
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKey)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', handleKey)
      document.body.style.overflow = ''
    }
  }, [onClose])

  if (!photo) return null

  const dateStr = new Date(photo.date).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex flex-col bg-black/90"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      style={{
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        overscrollBehavior: 'contain',
      }}
    >
      {/* Header — X in normal flow so it never overlaps the status bar. */}
      <div className="flex justify-end p-3 flex-shrink-0">
        <button
          onClick={onClose}
          className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Date sits above the image. flex-shrink-0 so the image wrapper
          below gets every bit of remaining height. */}
      <p
        className="text-center text-white/70 text-sm font-mono flex-shrink-0 pb-2 px-4"
        dir="auto"
        onClick={(e) => e.stopPropagation()}
      >
        {dateStr}
      </p>

      {/* Image wrapper — grows to fill remaining vertical space, centers
          the actual <img> within. object-contain keeps aspect ratio so
          the image scales up to whatever fits, no dead band. */}
      <div
        className="flex-1 min-h-0 flex items-center justify-center px-4"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={photo.signedUrl}
          alt={photo.caption || photo.pose}
          className="max-w-full max-h-full object-contain rounded-lg"
        />
      </div>

      {photo.caption && (
        <p
          className="text-center text-white/90 text-sm italic max-w-prose mx-auto flex-shrink-0 px-6 pt-3 pb-4"
          dir="auto"
          onClick={(e) => e.stopPropagation()}
        >
          {photo.caption}
        </p>
      )}
    </div>,
    document.body,
  )
}
